/* «Solgt tidligere» på kolleksjonssidene (snippets/solgt-tidligere.liquid, logikk docs/varesystem/logikk/solgt-tidligere.md).
   Lastes av sections/main-collection-products.liquid når delen er på, utenfor det filtrene erstatter, så elementet også virker når
   filtrene tegner det inn senere.
   Henter én arkivside av gangen med section_id og legger varekortene inn under varene på lager. Første henting starter når delen nærmer
   seg skjermen. Har kategorien flere sider (data-venter), venter skriptet til temaets custom-pagination har lastet siste side.
   Omdirigering (taggen finnes ikke på noen vare), 404 eller et svar som ikke er arkivet: delen forblir skjult. Nettverksfeil eller 5xx:
   delen vises med lenken «Se alle solgte» til arkivet. */
if (!customElements.get('gg-solgt-tidligere')) {
  // Det som ikke gir mening på en solgt vare: kjøpsknappen, favoritthjertet, rabattmerket, førprisen og enhetsprisen.
  // Igjen står bildet, tittelen, merket «Utsolgt», siste pris i butikken og lenken til varesiden.
  const FJERN = '.card__quick-add, .gg-fav-knapp, .product-label--sale, .price__was, .unit-price';

  class GgSolgtTidligere extends HTMLElement {
    connectedCallback() {
      if (!this.klar) {
        this.klar = true;
        this.side = 0;
        this.liste = this.querySelector('[data-gg-solgt-liste]');
        this.mer = this.querySelector('[data-gg-solgt-mer]');
        this.alle = this.querySelector('[data-gg-solgt-alle]');
        this.reserve = this.querySelector('[data-gg-solgt-reserve]');
        this.vedKlikk = (e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          this.hent(true);
        };
        if (this.mer) this.mer.addEventListener('click', this.vedKlikk);
      }
      // Første visning er ferdig (samme node koblet til igjen): ikke start på nytt. Pågår en henting som ble avbrutt da noden
      // ble flyttet, starter den igjen når avbruddet er ferdigbehandlet (finally i hent).
      if (this.ferdig) return;
      if (this.henter) { this.startIgjen = true; return; }
      this.start();
    }

    start() {
      if (this.hasAttribute('data-venter')) this.ventPaSisteSide();
      else this.ventPaSkjermen();
    }

    disconnectedCallback() {
      if (this.observator) { this.observator.disconnect(); this.observator = null; }
      if (this.sideVakt) { this.sideVakt.disconnect(); this.sideVakt = null; }
      if (this.punkt) { this.punkt.remove(); this.punkt = null; }
      if (this.kontroller) this.kontroller.abort();
    }

    // Uendelig rulling eller «last flere»: custom-pagination fjerner .js-pagination når siste side er lagt inn
    ventPaSisteSide() {
      const sider = this.closest('custom-pagination');
      const ferdig = () => !sider || !sider.querySelector('.js-pagination');
      if (ferdig()) { this.ventPaSkjermen(); return; }
      this.sideVakt = new MutationObserver(() => {
        if (!ferdig()) return;
        this.sideVakt.disconnect();
        this.sideVakt = null;
        this.ventPaSkjermen();
      });
      this.sideVakt.observe(sider, { childList: true, subtree: true });
      // Siste side kan ha kommet mellom første sjekk og observe
      if (ferdig() && this.sideVakt) { this.sideVakt.disconnect(); this.sideVakt = null; this.ventPaSkjermen(); }
    }

    ventPaSkjermen() {
      if (this.observator || this.henter) return;
      if (!('IntersectionObserver' in window)) { this.hent(false); return; }
      // Elementet er skjult (hidden) til kortene er inne og har ingen boks, så et punkt rett før det observeres
      this.punkt = document.createElement('div');
      this.punkt.setAttribute('aria-hidden', 'true');
      this.punkt.style.height = '1px';
      this.before(this.punkt);
      this.observator = new IntersectionObserver((treff) => {
        if (!treff.some((t) => t.isIntersecting)) return;
        this.observator.disconnect();
        this.observator = null;
        this.punkt.remove();
        this.punkt = null;
        this.hent(false);
      }, { rootMargin: '800px 0px' });
      this.observator.observe(this.punkt);
    }

    // Henter neste arkivside, og fortsetter over sider uten utsolgte kort til det kommer nye kort eller arkivet er slutt.
    // fraKunden: trykk på «Vis flere», bare da flyttes fokus.
    async hent(fraKunden) {
      if (this.henter || !this.liste) return;
      this.henter = true;
      this.kontroller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      if (this.mer) this.mer.setAttribute('aria-disabled', 'true');
      let forste = null;
      let flere = false;
      try {
        do {
          const neste = this.side + 1;
          const url = `${this.dataset.url}?section_id=${encodeURIComponent(this.dataset.seksjon)}&page=${neste}`;
          const svar = await fetch(url, { credentials: 'same-origin', signal: this.kontroller.signal });
          // Omdirigering (fulgt, eller rått 3xx-svar som i theme dev) og 404: ingenting å vise. 5xx og nettverksfeil gir reservelenken.
          if (svar.redirected || (svar.status >= 300 && svar.status < 400) || svar.status === 404) {
            throw Object.assign(new Error(`omdirigert eller borte: ${svar.status}`), { stille: true })
          }
          if (!svar.ok) throw new Error(`solgt-tidligere: ${svar.status}`);
          const doc = new DOMParser().parseFromString(await svar.text(), 'text/html');
          const markor = doc.querySelector('[data-gg-solgt-sider]');
          if (!markor) throw Object.assign(new Error('ikke arkivet'), { stille: true });
          const sider = parseInt(markor.dataset.ggSolgtSider || '0', 10);
          // Varer som likevel er tilgjengelige er merket data-gg-tilgjengelig i arkivet (product.available over alle variantene).
          // Klassen js-pagination-result fjernes: temaets custom-pagination legger nye varer etter siste element med den klassen
          const kort = Array.from(doc.querySelectorAll('li.js-pagination-result:not([data-gg-tilgjengelig])'))
            .map((li) => {
              const ny = document.importNode(li, true);
              ny.classList.remove('js-pagination-result');
              ny.classList.add('gg-solgt__vare');
              ny.querySelectorAll(FJERN).forEach((el) => el.remove());
              return ny;
            });
          kort.forEach((li) => this.liste.appendChild(li));
          if (!forste && kort.length) forste = kort[0];
          this.side = neste;
          flere = neste < sider;
        } while (!forste && flere);
        if (this.liste.children.length > 0) this.hidden = false;
        if (this.mer) {
          this.mer.hidden = !flere;
          this.mer.href = `${this.dataset.url}?page=${this.side + 1}`;
        }
        if (this.alle) this.alle.hidden = !flere;
        this.ferdig = true;
        // Etter «Vis flere»: fokus til første nye kort. Kom det ingen nye og knappen er borte, til siste kort, så fokus ikke forsvinner.
        const mal = forste || (fraKunden && this.mer && this.mer.hidden ? this.liste.lastElementChild : null);
        if (fraKunden && mal) {
          const lenke = mal.querySelector('a[href]:not([tabindex="-1"])') || mal.querySelector('a[href]');
          if (lenke) lenke.focus({ preventScroll: !forste });
        }
      } catch (feil) {
        if (feil && feil.name === 'AbortError') return;
        if (feil && feil.stille) {
          this.ferdig = true;
          if (this.liste.children.length === 0) this.hidden = true;
        } else if (this.liste.children.length === 0) {
          // Nettverks- eller serverfeil før noe er vist: overskriften og en vanlig lenke til arkivet
          this.ferdig = true;
          if (this.reserve) this.reserve.hidden = false;
          this.hidden = false;
        } else if (this.mer) {
          this.mer.hidden = false;
        }
      } finally {
        this.henter = false;
        this.kontroller = null;
        this.removeAttribute('aria-busy');
        if (this.mer) this.mer.removeAttribute('aria-disabled');
        if (this.startIgjen) {
          this.startIgjen = false;
          if (this.isConnected && !this.ferdig) this.start();
        }
      }
    }
  }
  customElements.define('gg-solgt-tidligere', GgSolgtTidligere);
}
